import axios from 'axios';
import { 
  PageDefinition, 
  CreatePageRequest, 
  UpdatePageRequest, 
  PageListResponse
} from '../types/pages';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface PageFilters {
  category?: string;
  search?: string;
  isActive?: boolean;
}

export class PageService {
  // Page CRUD operations
  static async createPage(pageData: CreatePageRequest): Promise<PageDefinition> {
    const response = await api.post('/pages', pageData);
    return response.data;
  }

  static async getPages(filters?: PageFilters & { page?: number; limit?: number }): Promise<PageListResponse> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/pages?${params.toString()}`);
    return response.data;
  }

  static async getPage(id: string): Promise<PageDefinition> {
    const response = await api.get(`/pages/${id}`);
    return response.data;
  }

  static async updatePage(id: string, pageData: Partial<UpdatePageRequest>): Promise<PageDefinition> {
    const response = await api.put(`/pages/${id}`, pageData);
    return response.data;
  }

  static async deletePage(id: string): Promise<void> {
    await api.delete(`/pages/${id}`);
  }

  static async duplicatePage(id: string, name: string): Promise<PageDefinition> {
    const response = await api.post(`/pages/${id}/duplicate`, { name });
    return response.data;
  }

  static async publishPage(id: string): Promise<PageDefinition> {
    const response = await api.post(`/pages/${id}/publish`);
    return response.data;
  }

  static async unpublishPage(id: string): Promise<PageDefinition> {
    const response = await api.post(`/pages/${id}/unpublish`);
    return response.data;
  }
}

export default PageService;